import { flexRender } from "@tanstack/react-table";
import classNames from "classnames";
import isString from "lodash/isString";
import { Header } from "PFComponents/table/table.types";
import { useTranslation } from "react-i18next";

import { TableData } from "../table";
import css from "../table.module.scss";
import { TableSorting } from "./table_sorting";

type ColumnHeaderProps<T extends TableData> = {
  header: Header<T, unknown>;
  compact?: boolean;
  enableMultiRowSelection?: boolean;
};

export const ColumnHeader = <TABLE_DATA extends TableData>({
  header,
  compact,
  enableMultiRowSelection
}: ColumnHeaderProps<TABLE_DATA>) => {
  const { t } = useTranslation("core", { keyPrefix: "components.table" });
  const canSort = header.column.getCanSort();
  const isSorted = header.column.getIsSorted();

  const { align, headerClassName } = header.column.columnDef.meta || {};

  const Component = canSort ? "button" : "div";

  const columnName =
    header.column.columnDef.meta?.ariaLabel ||
    (isString(header.column.columnDef.header) && header.column.columnDef.header) ||
    header.column.id ||
    t("column");

  return (
    <th
      scope="col"
      aria-label={columnName}
      className={classNames(headerClassName, {
        [css.compactHeader]: compact,
        [css.withSelectAll]: enableMultiRowSelection
      })}
      style={{
        // TODO: [SP-3205] Is there a better way to achieve 100% width when size is not passed?
        // "calc()" is needed to make "width: inherit" working in css.headerButton
        width: header.getSize() !== 0 ? header.getSize() : "calc(100% - 0.01px)"
      }}
    >
      <Component
        className={classNames(css.headerButton, {
          [css.sortableColumn]: canSort,
          [css.alignRight]: align === "right"
        })}
        onClick={header.column.getToggleSortingHandler()}
        {...(canSort
          ? {
              "aria-label": t("changeOrderFor", { columnName })
            }
          : {})}
      >
        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        {canSort && <TableSorting isSorted={isSorted} />}
      </Component>
    </th>
  );
};
